import type { PageSkeletonProps } from './PageSkeleton'

const CONCEPT_SECTIONS: Record<string, PageSkeletonProps> = {
    overview: { variant: 'section', fields: 12 },
    general: { variant: 'editor', editors: 6 },
    detailedInfo: { variant: 'editor', editors: 5 },
    baseInfo: { variant: 'section', fields: 6 },
    categorization: { variant: 'section', fields: 8 },
    approval: { variant: 'section', fields: 6 },
    members: { variant: 'section', fields: 4 },
    notes: { variant: 'section', fields: 4 },
    cost: { variant: 'list', rows: 8, columns: 6 },
    calculations: { variant: 'list', rows: 8, columns: 6 },
    'pre-calculation': { variant: 'list', rows: 10, columns: 7 },
    productions: { variant: 'list', rows: 6, columns: 5 },
    profitability: { variant: 'profitability' },
}

const WORK_SECTIONS: Record<string, PageSkeletonProps> = {
    overview: { variant: 'section', fields: 10 },
    misc: { variant: 'section', fields: 10 },
    'launch-brief': { variant: 'editor', editors: 6 },
    categorization: { variant: 'section', fields: 8 },
    corrections: { variant: 'section', fields: 6 },
    costs: { variant: 'section', fields: 8 },
    royalty: { variant: 'section', fields: 6 },
    'review-quotes': { variant: 'section', fields: 4 },
    assets: { variant: 'list', rows: 8 },
    contracts: { variant: 'list', rows: 6 },
    productions: { variant: 'list', rows: 6, columns: 5 },
    rights: { variant: 'list', rows: 8 },
    team: { variant: 'list', rows: 6, columns: 4 },
    calculation: { variant: 'list', rows: 8, columns: 6 },
    profitability: { variant: 'profitability' },
}

const EDITION_SECTIONS: Record<string, PageSkeletonProps> = {
    overview: { variant: 'section', fields: 12 },
    'content-press-sales': { variant: 'editor', editors: 4 },
    'content-title-portrait': { variant: 'editor', editors: 4 },
    'content-categorization': { variant: 'section', fields: 6 },
    'content-editorial': { variant: 'section', fields: 4 },
    'content-education': { variant: 'section', fields: 4 },
    'content-links': { variant: 'list', rows: 6, columns: 4 },
    comments: { variant: 'section', fields: 4 },
    copyproduction: { variant: 'section', fields: 6 },
    copytechnicalproduction: { variant: 'section', fields: 6 },
    distribution: { variant: 'section', fields: 6 },
    'distribution-logs': { variant: 'list', rows: 10, columns: 5 },
    erp: { variant: 'section', fields: 6 },
    'general-bookclubs': { variant: 'list', rows: 8, columns: 5 },
    'general-costs': { variant: 'section', fields: 8 },
    'general-misc': { variant: 'section', fields: 10 },
    'general-price-and-cost': { variant: 'section', fields: 8 },
    'general-printrun': { variant: 'section', fields: 6 },
    'general-rights': { variant: 'section', fields: 6 },
    'general-royalty-levels': { variant: 'section', fields: 6 },
    'general-sales': { variant: 'list', rows: 8, columns: 6 },
    'general-schedule': { variant: 'list', rows: 8, columns: 5 },
    'layout-binding': { variant: 'section', fields: 8 },
    'layout-composition': { variant: 'section', fields: 6 },
    'layout-cover': { variant: 'section', fields: 8 },
    'layout-inside': { variant: 'section', fields: 8 },
    'layout-measurements': { variant: 'section', fields: 6 },
    'layout-technicalproduction': { variant: 'section', fields: 6 },
    layout: { variant: 'section', fields: 6 },
    technicaldescription: { variant: 'section', fields: 6 },
    'print-orders': { variant: 'section', fields: 6 },
    printchanges: { variant: 'section', fields: 6 },
    publish: { variant: 'section', fields: 4 },
    unpublish: { variant: 'section', fields: 4 },
    assets: { variant: 'list', rows: 8 },
    team: { variant: 'list', rows: 6, columns: 4 },
    profitability: { variant: 'profitability' },
}

const CONTACT_SECTIONS: Record<string, PageSkeletonProps> = {
    overview: { variant: 'section', fields: 10 },
    details: { variant: 'section', fields: 10 },
    texts: { variant: 'editor', editors: 4 },
    addresses: { variant: 'section', fields: 8 },
    lists: { variant: 'section', fields: 4 },
    notes: { variant: 'section', fields: 4 },
    retailer: { variant: 'section', fields: 6 },
    royalty: { variant: 'section', fields: 6 },
    agreements: { variant: 'list', rows: 8 },
    assets: { variant: 'list', rows: 8 },
    companycontacts: { variant: 'list', rows: 8, columns: 5 },
    concepts: { variant: 'list', rows: 8, columns: 5 },
    links: { variant: 'list', rows: 6, columns: 4 },
    productions: { variant: 'list', rows: 8, columns: 6 },
    works: { variant: 'list', rows: 8, columns: 5 },
}

const ASSET_SECTIONS: Record<string, PageSkeletonProps> = {
    overview: { variant: 'section', fields: 6 },
    general: { variant: 'section', fields: 8 },
    replace: { variant: 'section', fields: 4 },
    connections: { variant: 'list', rows: 8, columns: 5 },
}

const ADMIN_SECTIONS: Record<string, PageSkeletonProps> = {
    overview: { variant: 'cardList', items: 8, fieldsPerCard: 1 },
    costtypes: { variant: 'list', rows: 16, columns: 7 },
    eanseries: { variant: 'formGrid', fields: 4, rows: 12, columns: 4 },
    emailtemplates: { variant: 'list', rows: 2, columns: 2 },
    printerprices: { variant: 'upload', fields: 4, helpLines: 3 },
    'production-plans': {
        variant: 'cardList',
        items: 7,
        cardColumns: 2,
        fieldsPerCard: 1,
        badge: true,
    },
    royaltyPriceRules: { variant: 'cardList', items: 7, fieldsPerCard: 3 },
    royaltySalesChannels: { variant: 'editableRows', rows: 7, toggles: 2, headerAction: true },
    tags: { variant: 'editableRows', rows: 11, toggles: 1 },
}

const ADMINISTRATION_SECTIONS: Record<string, PageSkeletonProps> = {
    ...ADMIN_SECTIONS,
    clients: { variant: 'grid', rows: 10 },
    users: { variant: 'grid', rows: 10 },
    metadata: { variant: 'grid', rows: 10 },
    access: { variant: 'section', fields: 8 },
    general: { variant: 'grid', rows: 8 },
    integrations: { variant: 'section', fields: 6 },
    documentation: { variant: 'section', fields: 4 },
    webhook: { variant: 'webhook' },
    events: { variant: 'webhook' },
    subscribers: { variant: 'stackedTables', sections: 2, rows: 8, columns: 7 },
    'token-configurations': { variant: 'stackedTables', sections: 2, rows: 6, columns: 8 },
    createretailerorders: { variant: 'upload', helpLines: 1 },
    importprices: { variant: 'upload', infoBar: true },
    importstockchanges: { variant: 'upload', tableRows: 3, tableColumns: 3 },
    importtransaction: { variant: 'upload', helpLines: 1, tableRows: 12, tableColumns: 4 },
}

const AGREEMENT_SECTIONS: Record<string, PageSkeletonProps> = {
    overview: { variant: 'agreementGeneral' },
    general: { variant: 'agreementGeneral' },
    terms: { variant: 'termsRules', rules: 1 },
    settlements: { variant: 'list', rows: 8, columns: 6 },
    transactions: { variant: 'list', rows: 10, columns: 6 },
}

const ROYALTY_SECTIONS: Record<string, PageSkeletonProps> = {
    overview: { variant: 'dataGrid', columns: 12, action: true },
    agreements: { variant: 'dataGrid', columns: 12, rows: 16, action: true },
    statements: { variant: 'dataGrid', columns: 8, rows: 8, action: true },
    paymentinformation: {
        variant: 'dataGrid',
        subTabs: 2,
        toolbar: 'search',
        columns: 6,
        action: true,
    },
    upcoming: { variant: 'dataGrid', toolbar: 'search', columns: 6, action: true },
    history: { variant: 'dataGrid', toolbar: 'search', columns: 6, action: true },
    errorlist: {
        variant: 'dataGrid',
        subTabs: 4,
        toolbar: 'icons',
        columns: 4,
        footer: 'pagination',
    },
    terms: { variant: 'dataGrid', toolbar: 'icons', columns: 4, footer: 'pagination' },
    contacts: { variant: 'dataGrid', toolbar: 'icons', columns: 4, footer: 'pagination' },
    transactions: { variant: 'dataGrid', toolbar: 'icons', columns: 4, footer: 'pagination' },
    backgroundjobs: { variant: 'dataGrid', toolbar: 'none', columns: 7, rows: 16 },
    reports: {
        variant: 'dataGrid',
        subTabs: 4,
        toolbar: 'search',
        columns: 8,
        footer: 'pagination',
    },
    settlements: { variant: 'dataGrid', toolbar: 'search', columns: 8, footer: 'pagination' },
    advances: { variant: 'dataGrid', toolbar: 'search', columns: 8, footer: 'pagination' },
    'upcoming-settlements': {
        variant: 'dataGrid',
        toolbar: 'search',
        columns: 8,
        footer: 'pagination',
    },
}

const AREA_SECTIONS: Record<string, Record<string, PageSkeletonProps>> = {
    concept: CONCEPT_SECTIONS,
    work: WORK_SECTIONS,
    edition: EDITION_SECTIONS,
    contact: CONTACT_SECTIONS,
    assets: ASSET_SECTIONS,
    admin: ADMIN_SECTIONS,
    administration: ADMINISTRATION_SECTIONS,
    royalty: ROYALTY_SECTIONS,
}

const FLAT_AREAS = ['admin', 'administration', 'royalty']

const FALLBACK: PageSkeletonProps = { variant: 'section' }

export function sectionSkeletonProps(pathname: string): PageSkeletonProps {
    const segments = pathname.split('/').filter(Boolean)
    if (segments.length === 0) return FALLBACK

    if (segments.includes('agreement')) {
        if (segments.includes('version')) return { variant: 'ruleDetails' }

        const agreementIndex = segments.indexOf('agreement')
        const page =
            segments.length > agreementIndex + 2
                ? (segments[segments.length - 1] ?? '')
                : 'overview'
        return AGREEMENT_SECTIONS[page] ?? FALLBACK
    }

    const isEdition = segments.includes('edition')
    const area = isEdition ? 'edition' : segments[0]

    if (FLAT_AREAS.includes(area)) {
        const page = segments.length > 1 ? (segments[segments.length - 1] ?? '') : 'overview'
        return AREA_SECTIONS[area]?.[page] ?? FALLBACK
    }

    const editionIndex = segments.indexOf('edition')
    const depth = isEdition ? segments.length - (editionIndex + 2) : segments.length - 2
    const last = depth > 0 ? (segments[segments.length - 1] ?? '') : 'overview'

    return AREA_SECTIONS[area]?.[last] ?? FALLBACK
}
